import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Star, Search, Camera, Upload, QrCode, CheckCircle2, Link2, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import QrScanner from "@/components/QrScanner";
import { PayQrCard } from "@/components/PayQrCard";

const WORKER_TYPES = ["petrol_attendant", "car_guard", "trolley_assistant", "waiter", "general"];

function toBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function PhotoPicker({ value, onChange }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (f) onChange(await toBase64(f));
  };
  return (
    <div className="flex items-center gap-4">
      {value ? <img src={value} alt="" className="h-16 w-16 rounded-xl object-cover ring-2 ring-primary/20" data-testid="worker-photo-preview" /> :
        <div className="grid h-16 w-16 place-items-center rounded-xl bg-secondary text-muted-foreground"><Camera className="h-6 w-6" /></div>}
      <div className="flex flex-1 gap-2">
        <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={pick} className="hidden" data-testid="worker-photo-camera" />
        <input ref={galRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid="worker-photo-gallery" />
        <Button type="button" variant="outline" size="sm" onClick={() => camRef.current?.click()} className="flex-1 gap-1.5 rounded-full"><Camera className="h-4 w-4" /> Take photo</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => galRef.current?.click()} className="flex-1 gap-1.5 rounded-full"><Upload className="h-4 w-4" /> Upload</Button>
      </div>
    </div>
  );
}

const statusBadge = (s) => s === "pending" ? "bg-brand-orange/15 text-brand-orange" : "bg-primary/10 text-primary";

function OnboardDialog({ workplaces, onDone }) {
  const empty = { name: "", email: "", password: "", phone: "", id_number: "", bank_name: "", bank_account: "", worker_type: "petrol_attendant", workplace_id: "", place_of_work: "", photo: "", link_card_code: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, workplace_id: form.workplace_id || null, link_card_code: form.link_card_code || null };
      const { data } = await api.post("/admin/workers", payload);
      toast.success(`${data.name} onboarded · Pay code ${data.app_code}${data.linked_card ? " · Card " + data.linked_card + " linked" : ""}`);
      setOpen(false); setForm(empty); onDone();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-worker-button" className="rounded-full bg-brand-navy font-semibold text-white hover:bg-brand-navy/90"><Plus className="mr-1 h-4 w-4" /> Onboard worker</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Onboard a worker</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Photo (camera or device)</Label>
            <div className="mt-1.5"><PhotoPicker value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Full name</Label><Input required value={form.name} onChange={set("name")} data-testid="worker-name-input" className="mt-1" /></div>
            <div><Label>Email</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="worker-email-input" className="mt-1" /></div>
            <div><Label>Password</Label><Input value={form.password} onChange={set("password")} data-testid="worker-password-input" className="mt-1" placeholder="Default Worker123!" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="worker-phone-input" className="mt-1" /></div>
            <div><Label>ID number</Label><Input value={form.id_number} onChange={set("id_number")} data-testid="worker-id-input" className="mt-1" /></div>
            <div>
              <Label>Worker type</Label>
              <Select value={form.worker_type} onValueChange={(v) => setForm({ ...form, worker_type: v })}>
                <SelectTrigger className="mt-1" data-testid="worker-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{WORKER_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Bank</Label><Input value={form.bank_name} onChange={set("bank_name")} data-testid="worker-bank-input" className="mt-1" /></div>
            <div><Label>Account</Label><Input value={form.bank_account} onChange={set("bank_account")} data-testid="worker-account-input" className="mt-1" /></div>
          </div>
          <div>
            <Label>Workplace (corporate)</Label>
            <Select value={form.workplace_id} onValueChange={(v) => setForm({ ...form, workplace_id: v })}>
              <SelectTrigger className="mt-1" data-testid="worker-workplace-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>{workplaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Place of work (label)</Label><Input value={form.place_of_work} onChange={set("place_of_work")} data-testid="worker-pow-input" className="mt-1" placeholder="Petrol Attendant – Sasol N1 North" /></div>

          <div className="rounded-xl border border-border/60 bg-secondary/40 p-4">
            <div className="flex items-center gap-2 font-heading font-bold"><QrCode className="h-4 w-4 text-primary" /> Link lanyard / NFC card</div>
            <p className="mt-1 text-xs text-muted-foreground">Scan the pre-printed card to link it now. Optional — an in-app pay code is always auto-assigned.</p>
            {form.link_card_code ? (
              <div className="mt-3 space-y-3">
                <div className="flex items-center justify-between rounded-lg bg-primary/10 px-3 py-2 text-sm font-semibold text-primary" data-testid="onboard-scanned-code">
                  <span>{form.link_card_code}</span>
                  <button type="button" onClick={() => setForm({ ...form, link_card_code: "" })} className="text-xs underline">clear</button>
                </div>
                <PayQrCard code={form.link_card_code} workerName={form.name} />
                <p className="text-center text-xs text-muted-foreground">This QR opens the worker's payment page once you finish onboarding.</p>
              </div>
            ) : (
              <div className="mt-3"><QrScanner testId="onboard-scanner" onResult={(code) => { setForm((s) => ({ ...s, link_card_code: code })); toast.success(`Card ${code} ready to link`); }} /></div>
            )}
          </div>

          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="worker-submit-button" className="rounded-full bg-brand-navy font-semibold text-white hover:bg-brand-navy/90">{busy ? "Saving…" : "Onboard worker"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function WorkerDetailDialog({ workerId, workplaces, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [relinking, setRelinking] = useState(false);
  const [qrCode, setQrCode] = useState(null);

  const load = () => {
    if (!workerId) return;
    api.get(`/admin/workers/${workerId}`).then((r) => {
      setD(r.data);
      const w = r.data.worker;
      setForm({ name: w.name || "", phone: w.phone || "", bank_name: w.bank_name || "", bank_account: w.bank_account || "", worker_type: w.worker_type || "general", workplace_id: w.workplace_id || "", place_of_work: w.place_of_work || "", photo: w.photo || "", status: w.status || "active" });
    }).catch(() => {});
  };
  useEffect(load, [workerId]);
  if (!workerId) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/workers/${workerId}`, { ...form, workplace_id: form.workplace_id || null });
      toast.success("Worker updated");
      onChanged(); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const linkByCode = async (code) => {
    try {
      await api.post("/admin/cards/link", { worker_id: workerId, card_code: code });
      toast.success(`Card ${code} linked`);
      setRelinking(false); setQrCode(code.toUpperCase()); load(); onChanged();
    } catch (err) { toast.error(apiError(err)); }
  };
  const unlink = async (cardId) => {
    try { await api.post(`/admin/cards/${cardId}/unlink`); toast.success("Card unlinked"); load(); onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const actCr = async (rid, action) => {
    try { await api.post(`/admin/change-requests/${rid}/${action}`); toast.success(action === "approve" ? "Change applied" : "Rejected"); load(); onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const appCard = d?.cards?.find((c) => c.type === "app");
  const physCards = d?.cards?.filter((c) => c.type !== "app") || [];

  return (
    <Dialog open={!!workerId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="worker-detail-dialog">
        <DialogHeader><DialogTitle className="font-heading">Worker profile</DialogTitle></DialogHeader>
        {!form ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="space-y-4">
            <div><Label className="text-xs">Photo</Label><div className="mt-1.5"><PhotoPicker value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} /></div></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="edit-name-input" className="mt-1" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="edit-phone-input" className="mt-1" /></div>
              <div><Label>Bank</Label><Input value={form.bank_name} onChange={set("bank_name")} className="mt-1" /></div>
              <div><Label>Account</Label><Input value={form.bank_account} onChange={set("bank_account")} className="mt-1" /></div>
              <div>
                <Label>Worker type</Label>
                <Select value={form.worker_type} onValueChange={(v) => setForm({ ...form, worker_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="edit-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{WORKER_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["pending", "active", "suspended"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Workplace</Label>
              <Select value={form.workplace_id} onValueChange={(v) => setForm({ ...form, workplace_id: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-workplace-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{workplaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Place of work</Label><Input value={form.place_of_work} onChange={set("place_of_work")} className="mt-1" /></div>

            <div className="rounded-xl border border-border/60 bg-secondary/40 p-4">
              <p className="font-heading font-bold">Cards</p>
              {appCard && (
                <div className="mt-2 rounded-lg bg-primary/10 px-3 py-2 text-sm" data-testid="detail-app-card">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-primary">App pay code · {appCard.code}</span>
                    <button onClick={() => setQrCode(qrCode === appCard.code ? null : appCard.code)} className="rounded-md border border-border bg-white p-1.5" data-testid="app-card-qr-toggle" title="Show QR & link"><QrCode className="h-3.5 w-3.5" /></button>
                  </div>
                  {qrCode === appCard.code && <div className="mt-3"><PayQrCard code={appCard.code} workerName={form.name} /></div>}
                </div>
              )}
              {physCards.map((c) => (
                <div key={c.id} className="mt-2 rounded-lg bg-card px-3 py-2 text-sm" data-testid="detail-phys-card">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-semibold">{c.code} <Badge variant="secondary" className="ml-1 uppercase">{c.type}</Badge></span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setQrCode(qrCode === c.code ? null : c.code)} className="rounded-md border border-border bg-white p-1.5" data-testid={`phys-card-qr-toggle-${c.code}`} title="Show QR & link"><QrCode className="h-3.5 w-3.5" /></button>
                      <Button size="sm" variant="outline" onClick={() => unlink(c.id)} className="rounded-full">Unlink</Button>
                    </div>
                  </div>
                  {qrCode === c.code && <div className="mt-3"><PayQrCard code={c.code} workerName={form.name} /></div>}
                </div>
              ))}
              {!relinking ? (
                <Button type="button" size="sm" variant="outline" onClick={() => setRelinking(true)} data-testid="relink-open" className="mt-3 gap-1.5 rounded-full"><Link2 className="h-4 w-4" /> Scan & link a card</Button>
              ) : (
                <div className="mt-3"><QrScanner testId="relink-scanner" onResult={linkByCode} /></div>
              )}
            </div>

            {d?.change_requests?.filter((r) => r.status === "pending").length > 0 && (
              <div className="rounded-xl border border-brand-orange/40 bg-brand-orange/5 p-4" data-testid="change-requests">
                <p className="font-heading font-bold text-brand-orange">Pending detail change</p>
                {d.change_requests.filter((r) => r.status === "pending").map((r) => (
                  <div key={r.id} className="mt-2 rounded-lg bg-card p-3 text-sm shadow-sm">
                    {r.bank_name && <p>Bank: <span className="font-semibold">{r.bank_name} {r.bank_account || ""}</span></p>}
                    {r.phone && <p>Phone: <span className="font-semibold">{r.phone}</span></p>}
                    {r.note && <p className="text-muted-foreground">{r.note}</p>}
                    {r.document && <a href={r.document} target="_blank" rel="noreferrer" className="text-primary underline">View proof document</a>}
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" onClick={() => actCr(r.id, "approve")} data-testid={`cr-approve-${r.id}`} className="rounded-full bg-brand-navy text-white hover:bg-brand-navy/90">Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => actCr(r.id, "reject")} className="rounded-full">Reject</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <DialogFooter>
              <Button onClick={save} disabled={busy} data-testid="edit-save-button" className="rounded-full bg-brand-navy font-semibold text-white hover:bg-brand-navy/90">{busy ? "Saving…" : "Save changes"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminWorkers() {
  const [workers, setWorkers] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [detailId, setDetailId] = useState(null);

  const load = () => {
    api.get("/admin/workers").then((r) => setWorkers(r.data)).catch(() => {});
    api.get("/admin/workplaces").then((r) => setWorkplaces(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const approve = async (id, e) => {
    e.stopPropagation();
    try { const { data } = await api.post(`/admin/workers/${id}/approve`); toast.success(`${data.message} · Pay code ${data.app_code}`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const filtered = workers.filter((w) => {
    if (filter === "pending" && w.status !== "pending") return false;
    if (filter === "flagged" && !w.flagged_for_qr) return false;
    if (filter === "active" && w.status !== "active") return false;
    const s = (w.name || "") + (w.email || "");
    return s.toLowerCase().includes(q.toLowerCase());
  });
  const pendingCount = workers.filter((w) => w.status === "pending").length;

  return (
    <div className="animate-fade-up" data-testid="admin-workers">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Workers</h1>
          <p className="mt-1 text-sm text-muted-foreground">Onboard workers, approve sign-ups and manage cards.</p>
        </div>
        <OnboardDialog workplaces={workplaces} onDone={load} />
      </div>

      {pendingCount > 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-brand-orange/40 bg-brand-orange/10 px-4 py-3 text-sm text-brand-orange" data-testid="pending-banner">
          <AlertTriangle className="h-4 w-4" /> {pendingCount} self sign-up{pendingCount > 1 ? "s" : ""} awaiting approval — approve to auto-assign a pay code and flag for a lanyard.
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="worker-search" placeholder="Search workers…" className="pl-9" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" data-testid="worker-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All workers</SelectItem>
            <SelectItem value="pending">Pending approval</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="flagged">Needs lanyard</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 overflow-x-auto rounded-2xl border border-border/60 bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Worker</TableHead><TableHead>Workplace</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Wallet</TableHead><TableHead className="text-right">Rating</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((w) => (
              <TableRow key={w.id} data-testid="worker-row" className="cursor-pointer" onClick={() => setDetailId(w.id)}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {w.photo ? <img src={w.photo} alt="" className="h-9 w-9 rounded-lg object-cover" /> : <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/15 text-sm font-bold text-primary">{w.name?.[0]}</div>}
                    <div>
                      <p className="font-semibold text-primary hover:underline" data-testid={`worker-link-${w.id}`}>{w.name}</p>
                      <p className="text-xs text-muted-foreground">{w.email}</p>
                    </div>
                    {w.flagged_for_qr && <Badge className="ml-1 border-0 bg-brand-orange/15 text-brand-orange" title="Needs lanyard">lanyard</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{w.workplace_name || "—"}</TableCell>
                <TableCell><Badge className={`border-0 capitalize ${statusBadge(w.status)}`}>{w.status || "active"}</Badge></TableCell>
                <TableCell className="text-right font-semibold text-primary">R{(w.wallet_balance || 0).toFixed(2)}</TableCell>
                <TableCell className="text-right"><span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-brand-cyan text-brand-cyan" />{w.rating_avg || "—"}</span></TableCell>
                <TableCell className="text-right">
                  {w.status === "pending"
                    ? <Button size="sm" onClick={(e) => approve(w.id, e)} data-testid={`approve-${w.id}`} className="gap-1 rounded-full bg-brand-navy text-white hover:bg-brand-navy/90"><CheckCircle2 className="h-3.5 w-3.5" /> Approve</Button>
                    : <span className="text-xs text-muted-foreground">Open</span>}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No workers found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <WorkerDetailDialog workerId={detailId} workplaces={workplaces} onClose={() => setDetailId(null)} onChanged={load} />
    </div>
  );
}
